// Switch: turns a setting on or off, and it takes effect at once. A recessed slot whose knob
// springs across; on, the slot fills gold and the knob goes dark. The words beside it brighten
// with it. docs/operations/design-standard.md, "Controls".
//
// A native checkbox with role=switch, clipped (sr-only): forms, labels and Space keep working,
// and the shared table's Enter-opens-the-row key skips it the way it skips every input.

import React, { useState } from 'react';
import type { LabelProps } from './Checkbox';
import { CONTROL_MOTION, PEER_FOCUS, TOUCH } from './motion';

let seq = 0;

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'role'> {
  /** What it turns on. Omit for a bare switch, and name it with aria-label. */
  label?: React.ReactNode;
  /** A quieter line under the label; spoken as the description. */
  description?: React.ReactNode;
  /** `start` puts the words first: a settings row reads its question, then the switch. */
  labelSide?: 'end' | 'start';
  /** Take the row's width, the words filling the space between. */
  fill?: boolean;
  /** `lg` on shop-floor and kiosk screens. */
  size?: 'md' | 'lg';
  /** `warn` where on is a caution (an override that should not stay on). */
  tone?: 'primary' | 'warn';
  /** The state in words beside the slot ("Off", "Allowed"), for screens read at arm's length. */
  states?: { off: string; on: string };
  /** On the wrapping label: a `title` hint, or an `onClick` that stops a click reaching a clickable row. Extra props on the control land on the hidden input, where neither works. */
  labelProps?: LabelProps;
}

const TRACK = { md: 'h-5 w-9', lg: 'h-6 w-11' };
const KNOB = { md: 'h-4 w-4', lg: 'h-5 w-5' };
const TRAVEL = { md: 'translate-x-4', lg: 'translate-x-5' };
const ON_FILL = { primary: 'bg-primary np-state-on-primary', warn: 'bg-amber-400 np-state-on-primary' };
const ON_WORD = { primary: 'text-primary', warn: 'text-amber-300' };

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      label,
      description,
      labelSide = 'end',
      fill = false,
      size = 'md',
      tone = 'primary',
      states,
      className = '',
      labelProps,
      checked,
      defaultChecked,
      onChange,
      disabled = false,
      'aria-describedby': describedBy,
      ...rest
    },
    ref,
  ) => {
    const [ids] = useState(() => {
      seq += 1;
      return { label: `ui-switch-${seq}-label`, note: `ui-switch-${seq}-note` };
    });
    const [own, setOwn] = useState(!!defaultChecked);
    const on = checked !== undefined ? !!checked : own;
    const named = !!(rest['aria-label'] || rest['aria-labelledby']);

    const track = disabled
      ? on ? 'bg-surface-container-highest' : 'bg-surface-container'
      : on ? ON_FILL[tone] : 'bg-outline-variant shadow-recess';
    const knob = disabled ? 'bg-on-surface-disabled' : on ? 'bg-on-primary' : 'bg-on-surface-variant shadow-key';
    const words = disabled ? 'text-on-surface-disabled' : on ? 'text-on-surface' : 'text-on-surface-variant';
    const text = size === 'lg' ? 'text-sm leading-5' : 'text-[13px] leading-[18px]';

    return (
      <label
        {...labelProps}
        className={`group/switch relative min-w-0 select-none gap-2.5 ${size === 'lg' ? 'min-h-11' : ''} ${fill ? 'flex w-full' : 'inline-flex max-w-full'} ${
          description ? 'items-start' : 'items-center'
        } ${labelSide === 'start' ? 'flex-row-reverse' : ''} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      >
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          className="peer sr-only"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          aria-labelledby={description !== undefined && label !== undefined && !named ? ids.label : undefined}
          aria-describedby={[describedBy, description !== undefined ? ids.note : undefined].filter(Boolean).join(' ') || undefined}
          onChange={(e) => {
            if (checked === undefined) setOwn(e.target.checked);
            onChange?.(e);
          }}
          {...rest}
        />
        <span
          aria-hidden="true"
          className={`relative inline-flex flex-none items-center rounded-full p-0.5 ${TRACK[size]} ${track} ${
            disabled ? '' : 'np-state group-hover/switch:[--np-layer:var(--np-state-hover,var(--np-hover-color))]'
          } ${CONTROL_MOTION} ${PEER_FOCUS} ${TOUCH} ${description ? 'mt-px' : ''}`}
        >
          <span
            className={`block rounded-full transition-[transform,background-color] duration-spring ease-spring motion-reduce:transition-[background-color] motion-reduce:duration-quick ${KNOB[size]} ${knob} ${
              on ? TRAVEL[size] : 'translate-x-0'
            }`}
          />
        </span>
        {states && (
          <span aria-hidden="true" className={`inline-grid flex-none text-xs font-medium ${description ? 'mt-0.5' : ''}`}>
            <span className={`[grid-area:1/1] transition-opacity duration-quick ease-emphasized ${on ? 'opacity-0' : 'opacity-100'} ${disabled ? 'text-on-surface-disabled' : 'text-on-surface-variant'}`}>
              {states.off}
            </span>
            <span className={`[grid-area:1/1] transition-opacity duration-quick ease-emphasized ${on ? 'opacity-100' : 'opacity-0'} ${disabled ? 'text-on-surface-disabled' : ON_WORD[tone]}`}>
              {states.on}
            </span>
          </span>
        )}
        {(label !== undefined || description !== undefined) && (
          <span className={`min-w-0 ${fill ? 'flex-1' : ''}`}>
            {label !== undefined && (
              <span id={ids.label} className={`block transition-[color] duration-quick ease-emphasized ${text} ${words}`}>
                {label}
              </span>
            )}
            {description !== undefined && (
              <span id={ids.note} className={`mt-0.5 block text-xs leading-4 ${disabled ? 'text-on-surface-disabled' : 'text-on-surface-variant'}`}>
                {description}
              </span>
            )}
          </span>
        )}
      </label>
    );
  },
);
Switch.displayName = 'Switch';

export default Switch;
